import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { getApiUrl, apiRequest } from '../../config/api';
import SuccessToast from '../SuccessToast';
import ConfirmDialog from '../ConfirmDialog';
import LegalDocumentsAdmin from './LegalDocumentsAdmin';

const permisosVacios = {
  botIds: [],
  chatBotIds: [],
  contratoBotIds: [],
  whatsappBotIds: [],
  instagramBotIds: [],
  templateBotIds: [],
  registroBotIds: [],
  atencionBotIds: [],
};

const BLOQUES_PERMISO = [
  ['permiso-chat', 'Acceso a Chat en vivo', 'chatBotIds'],
  ['permiso-contrato', 'Acceso a Contrato', 'contratoBotIds'],
  ['permiso-whatsapp', 'Acceso a WhatsApp', 'whatsappBotIds'],
  ['permiso-instagram', 'Acceso a Instagram', 'instagramBotIds'],
  ['permiso-plantillas', 'Acceso a plantillas', 'templateBotIds'],
  ['permiso-registros', 'Acceso a registros', 'registroBotIds'],
  ['permiso-atencion', 'Acceso a Atención al cliente', 'atencionBotIds'],
];

const idsNumericos = (ids) =>
  (ids || []).map(id => (typeof id === 'string' ? parseInt(id, 10) : Number(id))).filter(id => !isNaN(id));

const quitarBotDeListas = (usuario, botId) => {
  const siguiente = {
    ...usuario,
    botIds: usuario.botIds.filter(id => id !== botId),
  };
  BLOQUES_PERMISO.forEach(([, , campo]) => {
    siguiente[campo] = usuario[campo].filter(id => id !== botId);
  });
  return siguiente;
};

const coincideNombre = (bot, consulta) => {
  const q = consulta.trim().toLowerCase();
  return q.length > 0 && bot.botName.toLowerCase().includes(q);
};

// Se define fuera de AdminPanel: un componente declarado dentro se recrearía en cada
// render y React remontaría los checkboxes, perdiendo el foco al marcar.
const TarjetaPermisosBot = ({ bot, usuario, onQuitar, onTogglePermiso }) => (
  <div className="admin-bot-card" data-testid={`permiso-bot-${bot.botId}`}>
    <div className="admin-bot-card-header">
      <h4>{bot.botName}</h4>
      <button
        type="button"
        className="admin-bot-card-quitar"
        aria-label={`Quitar ${bot.botName}`}
        onClick={() => onQuitar(bot.botId)}
      >
        Quitar
      </button>
    </div>
    {BLOQUES_PERMISO.map(([, etiqueta, campo]) => (
      <label key={campo} className="admin-bot-card-option">
        <input
          type="checkbox"
          checked={usuario[campo].includes(bot.botId)}
          onChange={() => onTogglePermiso(campo, bot.botId)}
        />
        {etiqueta}
      </label>
    ))}
  </div>
);

const ListaTarjetasBot = ({ bots, usuario, buscadorId, onAsignar, onQuitar, onTogglePermiso }) => {
  const [consulta, setConsulta] = useState('');
  const asignados = bots.filter(bot => usuario.botIds.includes(bot.botId));
  const coincidencias = bots.filter(bot =>
    !usuario.botIds.includes(bot.botId) && coincideNombre(bot, consulta)
  );

  const asignar = (botId) => {
    onAsignar(botId);
    setConsulta('');
  };

  return (
    <div className="form-group">
      {bots.length === 0 ? (
        <p className="no-bots">No hay bots disponibles</p>
      ) : (
        <>
          <label htmlFor={buscadorId}>Asignar bot</label>
          <div className="admin-bot-search">
            <input
              id={buscadorId}
              type="search"
              placeholder="Buscar por nombre"
              value={consulta}
              onChange={e => setConsulta(e.target.value)}
              autoComplete="off"
            />
            {consulta.trim() ? (
              coincidencias.length === 0 ? (
                <p className="no-bots">Ningún bot coincide</p>
              ) : (
                <ul className="admin-bot-search-results">
                  {coincidencias.map(bot => (
                    <li key={bot.botId}>
                      <button type="button" onClick={() => asignar(bot.botId)}>
                        {bot.botName}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : null}
          </div>
          {asignados.length === 0 ? (
            <p className="no-bots">Ningún bot asignado</p>
          ) : (
            <div className="admin-bot-cards">
              {asignados.map(bot => (
                <TarjetaPermisosBot
                  key={bot.botId}
                  bot={bot}
                  usuario={usuario}
                  onQuitar={onQuitar}
                  onTogglePermiso={onTogglePermiso}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

function AdminPanel({
  adminUsers,
  adminBots,
  adminLoading,
  adminError,
  setAdminLoading,
  loadAdminData,
  onBotCreated,
  onBotDeleted,
}) {
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showCreateBotModal, setShowCreateBotModal] = useState(false);
  const [showDeleteBotModal, setShowDeleteBotModal] = useState(false);
  const [deleteBotPaso, setDeleteBotPaso] = useState('elegir');
  const [botAEliminarId, setBotAEliminarId] = useState('');
  const [nuevoBotNombre, setNuevoBotNombre] = useState('');
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'user', ...permisosVacios });
  const [editUser, setEditUser] = useState({ userId: null, username: '', email: '', password: '', role: 'user', ...permisosVacios });
  const [userToDelete, setUserToDelete] = useState(null);
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);

  // Un único alternador para las seis listas. Antes había una función por permiso,
  // idénticas salvo por el nombre del campo; con seis listas eso serían seis copias.
  const alternarPermiso = (setUser, campo, botId) => {
    setUser(actual => ({
      ...actual,
      [campo]: actual[campo].includes(botId)
        ? actual[campo].filter(id => id !== botId)
        : [...actual[campo], botId],
    }));
  };

  // Crear nuevo usuario
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setToastError(null);
    setToastExito(null);
    setAdminLoading(true);
    
    try {
      const payload = {
        ...newUser,
        botIds: idsNumericos(newUser.botIds),
        chatBotIds: idsNumericos(newUser.chatBotIds),
        contratoBotIds: idsNumericos(newUser.contratoBotIds),
        whatsappBotIds: idsNumericos(newUser.whatsappBotIds),
        instagramBotIds: idsNumericos(newUser.instagramBotIds),
        templateBotIds: idsNumericos(newUser.templateBotIds),
        registroBotIds: idsNumericos(newUser.registroBotIds),
        atencionBotIds: idsNumericos(newUser.atencionBotIds),
      };
      
      const response = await apiRequest(getApiUrl('/admin/users'), {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setToastExito('Usuario creado exitosamente');
        setShowCreateUserModal(false);
        setNewUser({ username: '', email: '', password: '', role: 'user', ...permisosVacios });
        loadAdminData(); // Recargar lista
      } else {
        setToastError(data.error || 'Error al crear usuario');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setToastError('Error de conexión');
    } finally {
      setAdminLoading(false);
    }
  };

  // Toggle bot en la selección
  const asignarBotNuevo = (botId) => {
    setNewUser(prev => (
      prev.botIds.includes(botId) ? prev : { ...prev, botIds: [...prev.botIds, botId] }
    ));
  };

  const asignarBotEditado = (botId) => {
    setEditUser(prev => (
      prev.botIds.includes(botId) ? prev : { ...prev, botIds: [...prev.botIds, botId] }
    ));
  };

  // Abrir modal de edición con datos del usuario
  const openEditUserModal = async (user) => {
    setToastError(null);
    setToastExito(null);
    
    // Cargar los bots asignados al usuario
    try {
      const response = await apiRequest(getApiUrl(`/admin/users/${user.userId}/bots`));
      let listas = { ...permisosVacios };
      if (response.ok) {
        const data = await response.json();
        listas = {
          botIds: idsNumericos(data.botIds),
          chatBotIds: idsNumericos(data.chatBotIds),
          contratoBotIds: idsNumericos(data.contratoBotIds),
          whatsappBotIds: idsNumericos(data.whatsappBotIds),
          instagramBotIds: idsNumericos(data.instagramBotIds),
          templateBotIds: idsNumericos(data.templateBotIds),
          registroBotIds: idsNumericos(data.registroBotIds),
          atencionBotIds: idsNumericos(data.atencionBotIds),
        };
      }
      
      setEditUser({
        userId: user.userId,
        username: user.userName,
        email: user.email,
        password: '',
        role: user.role === 'ROLE_ADMIN' ? 'admin' : 'user',
        ...listas,
      });
      setShowEditUserModal(true);
    } catch (error) {
      console.error('Error loading user bots:', error);
      setToastError('Error al cargar datos del usuario');
    }
  };

  // Guardar cambios del usuario
  const handleEditUser = async (e) => {
    e.preventDefault();
    setToastError(null);
    setToastExito(null);
    setAdminLoading(true);
    
    try {
      const payload = {
        ...editUser,
        botIds: idsNumericos(editUser.botIds),
        chatBotIds: idsNumericos(editUser.chatBotIds),
        contratoBotIds: idsNumericos(editUser.contratoBotIds),
        whatsappBotIds: idsNumericos(editUser.whatsappBotIds),
        instagramBotIds: idsNumericos(editUser.instagramBotIds),
        templateBotIds: idsNumericos(editUser.templateBotIds),
        registroBotIds: idsNumericos(editUser.registroBotIds),
        atencionBotIds: idsNumericos(editUser.atencionBotIds),
      };
      
      const response = await apiRequest(getApiUrl(`/admin/users/${editUser.userId}`), {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setToastExito('Usuario actualizado exitosamente');
        setShowEditUserModal(false);
        setEditUser({ userId: null, username: '', email: '', password: '', role: 'user', ...permisosVacios });
        loadAdminData(); // Recargar lista
      } else {
        setToastError(data.error || 'Error al actualizar usuario');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setToastError('Error de conexión');
    } finally {
    setAdminLoading(false);
  }
};

  const handleCreateBot = async (e) => {
    e.preventDefault();
    setToastError(null);
    setToastExito(null);
    setToastExito(null);
    setToastError(null);
    setAdminLoading(true);
    try {
      const response = await apiRequest(getApiUrl('/bots'), {
        method: 'POST',
        body: JSON.stringify({ botName: nuevoBotNombre }),
      });
      if (response.ok) {
        setShowCreateBotModal(false);
        setNuevoBotNombre('');
        setToastExito('Bot creado');
        loadAdminData();
        onBotCreated?.();
      } else {
        const data = await response.json();
        setToastError(data.error || 'Error al crear bot');
      }
    } catch (error) {
      setToastError('Error de conexión');
    } finally {
      setAdminLoading(false);
    }
  };

  const cerrarDeleteBotModal = () => {
    setShowDeleteBotModal(false);
    setDeleteBotPaso('elegir');
    setBotAEliminarId('');
  };

  const botAEliminar = adminBots.find(bot => String(bot.botId) === String(botAEliminarId));

  const handleDeleteBot = async () => {
    if (!botAEliminarId) {
      return;
    }
    setToastError(null);
    setToastExito(null);
    setToastExito(null);
    setToastError(null);
    setAdminLoading(true);
    try {
      const response = await apiRequest(getApiUrl(`/bots/${botAEliminarId}`), {
        method: 'DELETE',
      });
      if (response.ok) {
        const idBorrado = botAEliminarId;
        cerrarDeleteBotModal();
        setToastExito('Bot eliminado');
        loadAdminData();
        if (onBotDeleted) {
          onBotDeleted(idBorrado);
        } else {
          onBotCreated?.();
        }
      } else {
        const data = await response.json();
        setToastError(data.error || 'Error al eliminar bot');
      }
    } catch (error) {
      setToastError('Error de conexión');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setUserToDelete(null);
    setToastError(null);
    setToastExito(null);
    setAdminLoading(true);

    try {
      const response = await apiRequest(getApiUrl(`/admin/users/${userId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        setToastExito('Usuario eliminado exitosamente');
        loadAdminData(); // Recargar lista
      } else {
        const data = await response.json();
        setToastError(data.error || 'Error al eliminar usuario');
      }
    } catch (error) {
      setToastError('Error de conexión');
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <>
        <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
        <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />
        {userToDelete && (
          <ConfirmDialog
            title="Eliminar usuario"
            note="Esta acción no se puede deshacer."
            onCancel={() => setUserToDelete(null)}
            onConfirm={() => handleDeleteUser(userToDelete.id)}
          >
            ¿Eliminar al usuario <strong>{userToDelete.name}</strong>?
          </ConfirmDialog>
        )}
        <div className="admin-stack">
        <div className="admin-panel">
          <div className="admin-header">
            <h2>Bots</h2>
            <div className="admin-header-actions">
              <button
                className="admin-btn primary"
                onClick={() => setShowCreateBotModal(true)}
              >
                + Crear bot
              </button>
              <button
                className="admin-btn danger"
                onClick={() => {
                  setDeleteBotPaso('elegir');
                  setBotAEliminarId('');
                  setShowDeleteBotModal(true);
                }}
              >
                Eliminar bot
              </button>
            </div>
          </div>
        </div>
        <div className="admin-panel">
          <div className="admin-header">
            <h2>Administración de usuarios</h2>
            <button 
              className="admin-btn primary"
              onClick={() => setShowCreateUserModal(true)}
            >
              + Crear usuario
            </button>
          </div>
          
          {adminError && <div className="admin-alert error">{adminError}</div>}
          
          {adminLoading ? (
            <div className="admin-loading">Cargando...</div>
          ) : (
            <div className="admin-users-list">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Fecha Creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.map(user => (
                    <tr key={user.userId}>
                      <td>{user.userId}</td>
                      <td>{user.userName}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role === 'ROLE_ADMIN' ? 'admin' : 'user'}`}>
                          {user.role === 'ROLE_ADMIN' ? 'Admin' : 'Usuario'}
                        </span>
                      </td>
                      <td>{user.createdDate ? new Date(user.createdDate).toLocaleDateString() : '-'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button 
                            className="admin-btn-small"
                            onClick={() => openEditUserModal(user)}
                          >
                            Editar
                          </button>
                          <button 
                            className="admin-btn-small"
                            style={{ background: '#fee2e2', color: '#dc2626' }}
                            onClick={() => setUserToDelete({ id: user.userId, name: user.userName })}
                            disabled={adminLoading}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {adminUsers.length === 0 && (
                <p className="no-data">No hay usuarios registrados</p>
              )}
            </div>
          )}
        </div>
        <LegalDocumentsAdmin />
        </div>

        {showDeleteBotModal && createPortal(
          <div className="modal-overlay" onClick={cerrarDeleteBotModal}>
            <div
              className="modal-content admin-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-bot-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="delete-bot-title">Eliminar bot</h3>
                <button className="modal-close" onClick={cerrarDeleteBotModal}>×</button>
              </div>
              {deleteBotPaso === 'elegir' ? (
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="bot-a-eliminar">Bot a eliminar</label>
                    <select
                      id="bot-a-eliminar"
                      className="bm-select"
                      value={botAEliminarId}
                      onChange={e => setBotAEliminarId(e.target.value)}
                    >
                      <option value="">Elige un bot</option>
                      {adminBots.map(bot => (
                        <option key={bot.botId} value={String(bot.botId)}>
                          {bot.botName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="admin-btn secondary" onClick={cerrarDeleteBotModal}>
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="admin-btn danger"
                      onClick={() => setDeleteBotPaso('confirmar')}
                      disabled={!botAEliminarId}
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="modal-body">
                  <div className="confirm-modal-body">
                    <p>Vas a eliminar el siguiente bot</p>
                    <p className="delete-bot-nombre">{botAEliminar?.botName}</p>
                    <p className="confirm-modal-note">
                      Esta acción no se puede deshacer. Se irán canales, chats, scripts y tareas de este bot.
                    </p>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="admin-btn secondary" onClick={cerrarDeleteBotModal}>
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="admin-btn danger"
                      onClick={handleDeleteBot}
                      disabled={adminLoading}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

        {showCreateBotModal && createPortal(
          <div className="modal-overlay" onClick={() => setShowCreateBotModal(false)}>
            <div
              className="modal-content admin-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-bot-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="create-bot-title">Crear bot</h3>
                <button className="modal-close" onClick={() => setShowCreateBotModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreateBot}>
                <div className="form-group">
                  <label htmlFor="nuevo-bot-nombre">Nombre</label>
                  <input
                    id="nuevo-bot-nombre"
                    type="text"
                    value={nuevoBotNombre}
                    onChange={e => setNuevoBotNombre(e.target.value)}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="admin-btn secondary" onClick={() => setShowCreateBotModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="admin-btn primary">
                    Crear bot
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Modal para crear usuario */}
        {showCreateUserModal && createPortal(
          <div className="modal-overlay" onClick={() => setShowCreateUserModal(false)}>
            <div
              className="modal-content admin-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="create-user-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="create-user-title">Crear nuevo usuario</h3>
                <button className="modal-close" onClick={() => setShowCreateUserModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label>Nombre de usuario</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={e => setNewUser({...newUser, username: e.target.value})}
                    required
                    placeholder="usuario123"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={e => setNewUser({...newUser, email: e.target.value})}
                    required
                    placeholder="usuario@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Contraseña</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={e => setNewUser({...newUser, password: e.target.value})}
                    required
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="nuevo-usuario-rol">Rol</label>
                  <select
                    id="nuevo-usuario-rol"
                    className="bm-select"
                    value={newUser.role}
                    onChange={e => setNewUser({...newUser, role: e.target.value})}
                  >
                    <option value="user">Usuario</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <ListaTarjetasBot
                  bots={adminBots}
                  usuario={newUser}
                  buscadorId="buscar-bot-nuevo"
                  onAsignar={asignarBotNuevo}
                  onQuitar={(botId) => setNewUser(prev => quitarBotDeListas(prev, botId))}
                  onTogglePermiso={(campo, botId) => alternarPermiso(setNewUser, campo, botId)}
                />
                <div className="modal-actions">
                  <button type="button" className="admin-btn secondary" onClick={() => setShowCreateUserModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="admin-btn primary" disabled={adminLoading}>
                    {adminLoading ? 'Creando...' : 'Crear Usuario'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
        
        {/* Modal para editar usuario */}
        {showEditUserModal && createPortal(
          <div className="modal-overlay" onClick={() => setShowEditUserModal(false)}>
            <div
              className="modal-content admin-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="edit-user-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="edit-user-title">Editar Usuario</h3>
                <button className="modal-close" onClick={() => setShowEditUserModal(false)}>×</button>
              </div>
              <form onSubmit={handleEditUser}>
                <div className="form-group">
                  <label>Nombre de usuario</label>
                  <input
                    type="text"
                    value={editUser.username}
                    onChange={e => setEditUser({...editUser, username: e.target.value})}
                    required
                    placeholder="usuario123"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editUser.email}
                    onChange={e => setEditUser({...editUser, email: e.target.value})}
                    required
                    placeholder="usuario@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Nueva contraseña (dejar vacío para no cambiar)</label>
                  <input
                    type="password"
                    value={editUser.password}
                    onChange={e => setEditUser({...editUser, password: e.target.value})}
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editar-usuario-rol">Rol</label>
                  <select
                    id="editar-usuario-rol"
                    className="bm-select"
                    value={editUser.role}
                    onChange={e => setEditUser({...editUser, role: e.target.value})}
                  >
                    <option value="user">Usuario</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <ListaTarjetasBot
                  bots={adminBots}
                  usuario={editUser}
                  buscadorId="buscar-bot-editar"
                  onAsignar={asignarBotEditado}
                  onQuitar={(botId) => setEditUser(prev => quitarBotDeListas(prev, botId))}
                  onTogglePermiso={(campo, botId) => alternarPermiso(setEditUser, campo, botId)}
                />
                <div className="modal-actions">
                  <button type="button" className="admin-btn secondary" onClick={() => setShowEditUserModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="admin-btn primary" disabled={adminLoading}>
                    {adminLoading ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default AdminPanel;
